// Helpers for turning admin-entered links into safe URLs.
// Content is edited through the admin page, so every URL is treated as
// untrusted input: anything that doesn't match the expected shape is dropped
// rather than rendered.

/** Returns the URL if it is a plain http(s) link, otherwise undefined. */
export function safeHttpUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Accepts any common YouTube link (watch, youtu.be, embed, shorts) and returns
 * the embeddable player URL, or undefined if it isn't a YouTube video link.
 */
export function toYouTubeEmbedUrl(value?: string): string | undefined {
  const href = safeHttpUrl(value);
  if (!href) return undefined;
  const url = new URL(href);
  const host = url.hostname.replace(/^(www\.|m\.)/, '');
  let id: string | null = null;

  if (host === 'youtu.be') {
    id = url.pathname.slice(1);
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const [, kind, rest] = url.pathname.split('/');
    if (kind === 'watch') id = url.searchParams.get('v');
    else if (kind === 'embed' || kind === 'shorts' || kind === 'live') id = rest;
  }

  return id && YOUTUBE_ID.test(id) ? `https://www.youtube.com/embed/${id}` : undefined;
}

/**
 * Accepts the player URL from Bandcamp's "Share / Embed" code
 * (https://bandcamp.com/EmbeddedPlayer/...) and returns it, or undefined.
 */
export function toBandcampEmbedUrl(value?: string): string | undefined {
  const href = safeHttpUrl(value);
  if (!href) return undefined;
  const url = new URL(href);
  return url.protocol === 'https:' &&
    url.hostname === 'bandcamp.com' &&
    url.pathname.startsWith('/EmbeddedPlayer/')
    ? url.href
    : undefined;
}

const SPOTIFY_TYPES = ['track', 'album', 'playlist', 'artist', 'episode', 'show'] as const;
type SpotifyType = (typeof SPOTIFY_TYPES)[number];
const SPOTIFY_ID = /^[A-Za-z0-9]{22}$/;

/**
 * Accepts a Spotify share link (open.spotify.com/album/…, also with /intl-xx/
 * or /embed/), or a spotify:album:… URI, and returns the embeddable player URL.
 */
export function toSpotifyEmbedUrl(value?: string): { url: string; type: SpotifyType } | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  let type: string | undefined;
  let id: string | undefined;

  const uri = /^spotify:([a-z]+):([A-Za-z0-9]+)$/.exec(raw);
  if (uri) {
    [, type, id] = uri;
  } else {
    const href = safeHttpUrl(raw);
    if (!href) return undefined;
    const url = new URL(href);
    if (url.protocol !== 'https:' || url.hostname !== 'open.spotify.com') return undefined;
    const parts = url.pathname.split('/').filter(Boolean).filter((p) => !/^intl-[a-z-]+$/i.test(p) && p !== 'embed');
    [type, id] = parts;
  }

  if (!type || !id || !(SPOTIFY_TYPES as readonly string[]).includes(type) || !SPOTIFY_ID.test(id)) return undefined;
  return { url: `https://open.spotify.com/embed/${type}/${id}`, type: type as SpotifyType };
}

/** Image paths come from the CMS media library: site-relative paths only. */
export function safeImagePath(value?: string): string | undefined {
  if (!value) return undefined;
  const path = value.trim();
  return path.startsWith('/') && !path.startsWith('//') ? path : undefined;
}

/** Parses a YYYY-MM-DD date as a local calendar date (no timezone shift). */
export function parseLocalDate(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
